import { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "../context/ThemeContext";
import { logoutUser } from "../pages/login/functions";
import CodeInput from "./CodeInput";
import { resolveSecurityChallenge, useSecurityChallengeStore } from "./securityChallengeStore";
import {
  fetchSecurityStatus,
  securityErrorOf,
  sendChallengeCode,
  verifyChallenge,
  type ChallengeMethod,
  type CodeSent,
} from "./securityService";
import { setSecurityStatus } from "./securityStatus";
import { useSecurityText } from "./useSecurityText";

/**
 * Diálogo que pide un código de verificación. Vive una sola vez en `App` y lo
 * abre `requestSecurityChallenge()`: al iniciar sesión con 2FA y antes de una
 * operación de riesgo. Los métodos que ofrece los decide el backend
 * (`status.challenge.methods`), no la pantalla.
 */
export default function SecurityChallengeModal() {
  const request = useSecurityChallengeStore((state) => state.request);
  const { isDarkMode } = useTheme();
  const { lang, tr } = useSecurityText();

  const [methods, setMethods] = useState<ChallengeMethod[] | null>(null);
  const [method, setMethod] = useState<ChallengeMethod | null>(null);
  const [code, setCode] = useState("");
  const [sent, setSent] = useState<CodeSent | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Un método por código que ya se envió solo no se reenvía al volver a él.
  const autoSent = useRef<Set<ChallengeMethod>>(new Set());

  const isLogin = request?.purpose === "login";

  const methodLabel = (m: ChallengeMethod) =>
    ({
      totp: tr("App de códigos", "Authenticator app"),
      sms: tr("SMS", "Text message"),
      email: tr("Correo", "Email"),
      recovery: tr("Código de respaldo", "Backup code"),
    })[m];

  const methodIcon = (m: ChallengeMethod) =>
    ({ totp: "ri-shield-keyhole-line", sms: "ri-message-2-line", email: "ri-mail-line", recovery: "ri-key-2-line" })[m];

  const send = useCallback(
    async (target: "email" | "sms") => {
      setBusy(true);
      setError(null);
      try {
        const result = await sendChallengeCode(target);
        setSent(result);
        setCooldown(result.resendInSeconds);
      } catch (err) {
        const info = securityErrorOf(err);
        if (info.retryAfterSeconds) setCooldown(info.retryAfterSeconds);
        setError(info.message || tr("No se pudo enviar el código.", "The code could not be sent."));
      } finally {
        setBusy(false);
      }
    },
    // `tr` cambia de identidad en cada render; el idioma es lo que importa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang],
  );

  // Al abrirse: con qué puede contestar esta persona.
  useEffect(() => {
    if (!request) return;
    let active = true;
    setMethods(null);
    setMethod(null);
    setCode("");
    setSent(null);
    setCooldown(0);
    setError(null);
    autoSent.current = new Set();
    fetchSecurityStatus()
      .then((status) => {
        if (!active) return;
        const available = status.challenge.methods;
        setMethods(available);
        const preferred = status.challenge.preferred;
        setMethod(preferred && available.includes(preferred) ? preferred : available[0] || null);
      })
      .catch((err) => {
        if (!active) return;
        setMethods([]);
        setError(securityErrorOf(err).message || tr("No se pudo cargar la verificación.", "Could not load the verification."));
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  // Correo y SMS: el código sale solo la primera vez que se elige el método.
  useEffect(() => {
    if (!request || (method !== "email" && method !== "sms")) return;
    if (autoSent.current.has(method)) return;
    autoSent.current.add(method);
    void send(method);
  }, [request, method, send]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  if (!request) return null;

  // Cerrar el reto de INICIO de sesión sin dar el código es no haber entrado:
  // la sesión a medias se cierra (si no, la app quedaría abierta pero sin poder
  // pedir nada). En una operación de riesgo, cancelar sólo cancela la operación.
  const cancel = () => {
    const wasLogin = isLogin;
    resolveSecurityChallenge(false);
    if (wasLogin) void logoutUser();
  };

  const submit = async (value = code) => {
    if (!method || busy || !value.trim()) return;
    setBusy(true);
    setError(null);
    try {
      setSecurityStatus(await verifyChallenge(method, value.trim()));
      resolveSecurityChallenge(true);
    } catch (err) {
      const info = securityErrorOf(err);
      setCode("");
      setError(
        info.attemptsLeft !== undefined
          ? `${info.message || tr("El código no es correcto", "The code is not correct")} · ${tr(
              `te quedan ${info.attemptsLeft} intentos`,
              `${info.attemptsLeft} attempts left`,
            )}`
          : info.message || tr("El código no es correcto.", "The code is not correct."),
      );
    } finally {
      setBusy(false);
    }
  };

  const pick = (next: ChallengeMethod) => {
    setMethod(next);
    setCode("");
    setError(null);
    setSent(null);
  };

  const card = isDarkMode ? "bg-dark-card text-white" : "bg-white text-gray-900";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const operationName = request.label?.[lang] || request.label?.es;

  const hint = () => {
    if (method === "totp") {
      return tr(
        "Abre tu app de códigos y escribe los 6 dígitos que muestra.",
        "Open your authenticator app and enter the 6 digits it shows.",
      );
    }
    if (method === "recovery") {
      return tr(
        "Escribe uno de tus códigos de respaldo. Cada uno sirve una sola vez.",
        "Enter one of your backup codes. Each one works only once.",
      );
    }
    if (sent) {
      return tr(`Enviamos un código a ${sent.sentTo}. Caduca en 10 minutos.`, `We sent a code to ${sent.sentTo}. It expires in 10 minutes.`);
    }
    return method === "sms"
      ? tr("Te enviaremos un código por SMS.", "We will text you a code.")
      : tr("Te enviaremos un código a tu correo.", "We will email you a code.");
  };

  return (
    <div className="fixed inset-0 z-[10050] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="security-challenge-title">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className={`relative w-full max-w-md rounded-2xl p-6 shadow-2xl ${card}`}>
        <div className="mb-4 flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-500/20">
            <i className="ri-shield-check-line text-xl text-primary-500" />
          </div>
          <div>
            <h2 id="security-challenge-title" className="text-lg font-semibold">
              {isLogin ? tr("Verificación en dos pasos", "Two-step verification") : tr("Confirma que eres tú", "Confirm it is you")}
            </h2>
            <p className={`text-sm ${muted}`}>
              {isLogin
                ? tr("Tu cuenta está protegida con un segundo paso.", "Your account is protected with a second step.")
                : operationName
                  ? tr(`Vas a: ${operationName}.`, `You are about to: ${operationName}.`)
                  : tr("Esta operación es sensible.", "This operation is sensitive.")}
            </p>
          </div>
        </div>

        {methods === null ? (
          <div className={`flex items-center justify-center gap-2 py-8 text-sm ${muted}`}>
            <i className="ri-loader-4-line animate-spin" />
            {tr("Cargando…", "Loading…")}
          </div>
        ) : methods.length === 0 ? (
          <p className="rounded-lg bg-red-500/10 px-3 py-3 text-sm text-red-500">
            {error ||
              tr(
                "No hay un método de verificación disponible para tu cuenta. Pide ayuda a un administrador.",
                "There is no verification method available for your account. Ask an administrator for help.",
              )}
          </p>
        ) : (
          <>
            {methods.length > 1 && (
              <div className="mb-4 flex flex-wrap gap-2" role="tablist" aria-label={tr("Método de verificación", "Verification method")}>
                {methods.map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="tab"
                    aria-selected={method === m}
                    onClick={() => pick(m)}
                    className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      method === m
                        ? "border-primary-500 bg-primary-500/15 text-primary-500"
                        : isDarkMode
                          ? "border-dark-border text-gray-300 hover:bg-dark-surface"
                          : "border-gray-300 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <i className={methodIcon(m)} />
                    {methodLabel(m)}
                  </button>
                ))}
              </div>
            )}

            <p className={`mb-3 text-sm ${muted}`}>{hint()}</p>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                void submit();
              }}
            >
              <CodeInput
                value={code}
                onChange={setCode}
                onComplete={(value) => void submit(value)}
                variant={method === "recovery" ? "recovery" : "digits"}
                disabled={busy}
                autoFocus
                isDarkMode={isDarkMode}
                ariaLabel={tr("Código de verificación", "Verification code")}
              />

              {error && (
                <p className="mt-3 text-sm text-red-500" role="alert">
                  {error}
                </p>
              )}

              {(method === "email" || method === "sms") && (
                <button
                  type="button"
                  disabled={busy || cooldown > 0}
                  onClick={() => void send(method)}
                  className="mt-3 text-sm font-medium text-primary-500 hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
                >
                  {cooldown > 0
                    ? tr(`Reenviar código en ${cooldown} s`, `Resend code in ${cooldown} s`)
                    : tr("Reenviar código", "Resend code")}
                </button>
              )}

              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={cancel}
                  className={`rounded-lg px-4 py-2 text-sm font-medium ${isDarkMode ? "text-gray-300 hover:bg-dark-surface" : "text-gray-600 hover:bg-gray-100"}`}
                >
                  {isLogin ? tr("Cerrar sesión", "Sign out") : tr("Cancelar", "Cancel")}
                </button>
                <button
                  type="submit"
                  disabled={busy || !code.trim()}
                  className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-gray-900 transition-colors hover:bg-primary-400 disabled:opacity-50"
                >
                  {busy && <i className="ri-loader-4-line animate-spin" />}
                  {tr("Verificar", "Verify")}
                </button>
              </div>
            </form>
          </>
        )}

        {methods !== null && methods.length === 0 && (
          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={cancel}
              className={`rounded-lg px-4 py-2 text-sm font-medium ${isDarkMode ? "text-gray-300 hover:bg-dark-surface" : "text-gray-600 hover:bg-gray-100"}`}
            >
              {isLogin ? tr("Cerrar sesión", "Sign out") : tr("Cerrar", "Close")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
